import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function CareersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": "Agricultural Drone Pilot",
    "description": "Join our team of skilled pilots who care about the land they fly over. Meaningful work, real growth, and a crew that values what you bring.",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Greenfield Partners"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      }
    },
    "employmentType": "FULL_TIME",
    "qualifications": "FAA Part 107 certification (or willingness to obtain), experience with agricultural operations preferred"
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    certifications: '',
    equipment: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d63btn30723ekv2itb7g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        experience: '',
        certifications: '',
        equipment: '',
        message: '',
      });
    } catch (error) {
      console.error('Submission error:', error);
      // Optionally you could set an error state here to display UI feedback
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Pay',
      desc: 'Earn what your skills are worth with transparent, fair compensation',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Schedule',
      desc: 'Work when it makes sense—seasonal peaks with time to recharge',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Ongoing Training',
      desc: 'Sharpen your skills with continuous learning and certification support',
    },
    {
      icon: 'ri-team-line',
      title: 'Supportive Team',
      desc: 'Join a crew that has your back in the field and beyond',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Career Growth',
      desc: 'Clear paths to lead pilot, trainer, and operations roles',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Equipment',
      desc: 'Fly with well-maintained, professional-grade aircraft',
    },
  ];

  const missionTypes = [
    {
      icon: 'ri-plant-line',
      title: 'Crop Application',
      desc: 'Precision spraying for fungicides, herbicides, and nutrients across row crops and specialty fields',
    },
    {
      icon: 'ri-camera-lens-line',
      title: 'Field Surveys',
      desc: 'RGB and NDVI imaging for stand counts, stress detection, and yield mapping',
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Thermal Reconnaissance',
      desc: 'Wildlife population surveys and movement tracking for crop damage mitigation',
    },
    {
      icon: 'ri-landscape-line',
      title: 'Land Assessment',
      desc: 'Terrain analysis, drainage evaluation, and conservation planning flights',
    },
  ];

  const requirements = [
    'FAA Part 107 certification (or willingness to obtain)',
    'Strong understanding of airspace regulations and safety protocols',
    'Experience with agricultural operations preferred',
    'Reliable transportation to reach rural job sites',
    'Ability to work independently and communicate clearly',
    'Physical capability to handle equipment in field conditions',
    'Commitment to professionalism and attention to detail',
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <SEO
        title="Careers - Join Our Agricultural Drone Pilot Team | Greenfield Partners"
        description="We're building a team of skilled pilots who care about the land they fly over. If you want meaningful work, real growth, and a crew that values what you bring—this is your place."
        keywords="agricultural drone pilot jobs, FAA Part 107 careers, precision agriculture careers, drone operator jobs"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5ba6b21fda5e124e6e9b42f90d7be10a.jpg"
            alt="Drone pilot in field"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-gray-900/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-emerald-400 font-semibold tracking-wide mb-4">JOIN OUR TEAM</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 max-w-3xl leading-tight">
            Fly With Purpose.<br />
            Grow Your Career.
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mb-8 leading-relaxed">
            We're building a team of skilled pilots who care about the land they fly over. If you
            want meaningful work, real growth, and a crew that values what you bring—this is your
            place.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#apply"
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Apply Now
            </a>
            <a
              href="#learn-more"
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap border border-white/30"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Why Join Us */}
      <section id="learn-more" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-emerald-700 font-semibold tracking-wide mb-3">WHY FLY WITH US</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">More Than a Gig. A Real Career.</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We're not looking for contractors to fill slots. We're building a team of professionals
                who take pride in their craft and want to be part of something that matters.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our pilots work directly with farmers to protect crops, improve yields, and make a
                tangible difference on the land. You'll see the results of your work—healthier fields,
                better harvests, and grateful partners.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This isn&apos;t about flying drones for the sake of flying. It&apos;s about using your
                skills to help people who depend on the land for their livelihood.
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Agricultural%20drone%20flying%20low%20over%20green%20soybean%20field%20spraying%20crops%20early%20morning%20mist%20rural%20farmland%20professional%20precision%20agriculture%20warm%20golden%20light%20detailed%20equipment&width=600&height=500&seq=careers-why-1&orientation=portrait"
                  alt="Drone spraying crops"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-emerald-700 text-white p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">100+</p>
                <p className="text-emerald-100">Missions Completed Monthly</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Benefits */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">WHAT YOU GET</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Benefits That Actually Matter</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We invest in our pilots because your success is our success. Here&apos;s what you can
              expect when you join the team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-5">
                  <i className={`${benefit.icon} text-2xl text-emerald-700`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Mission Types */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">YOUR MISSIONS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">The Work You&apos;ll Do</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every flight has a purpose. Here are the types of missions you&apos;ll take on as part of
              our team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {missionTypes.map((mission, index) => (
              <div
                key={index}
                className="flex gap-6 p-8 bg-stone-50 rounded-lg border border-stone-200 hover:border-emerald-300 transition-colors"
              >
                <div className="w-16 h-16 bg-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${mission.icon} text-3xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{mission.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{mission.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* What We Look For */}
      <section className="py-24 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20drone%20pilot%20checking%20equipment%20before%20flight%20agricultural%20setting%20sunrise%20golden%20hour%20detailed%20inspection%20safety%20focused%20rural%20farmland%20background%20warm%20tones&width=600&height=500&seq=careers-req-1&orientation=portrait"
                  alt="Pilot checking equipment"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <p className="text-emerald-400 font-semibold tracking-wide mb-3">WHAT WE EXPECT</p>
              <h2 className="text-4xl font-bold mb-6">The Right Fit</h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                We&apos;re looking for pilots who take their work seriously—people who show up prepared,
                fly safely, and treat every acre like it matters. Because it does.
              </p>
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-emerald-700 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-200">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Career Path */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">YOUR PATH FORWARD</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Room to Grow</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start as a pilot. Build your skills. Take on more responsibility. We promote from within
              and invest in your development.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
            {[
              { title: 'Field Pilot', desc: 'Execute missions, build experience' },
              { title: 'Senior Pilot', desc: 'Lead complex operations' },
              { title: 'Lead Pilot', desc: 'Train others, manage regions' },
              { title: 'Operations Lead', desc: 'Shape strategy, grow the team' },
            ].map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="bg-white p-6 rounded-lg shadow-sm text-center min-w-[200px]">
                  <div className="w-12 h-12 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block w-12 h-0.5 bg-emerald-400"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Application Form */}
      <section id="apply" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">READY TO JOIN?</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Let&apos;s Start the Conversation</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tell us about yourself. No pressure—just an honest conversation about whether this is the
              right fit for both of us.
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Received</h3>
              <p className="text-gray-600">
                Thanks for reaching out. We&apos;ll review your information and get back to you soon.
              </p>
            </div>
          ) : (
            <form
              id="pilot-application-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-stone-50 p-8 md:p-12 rounded-lg border border-stone-200"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location / Region
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="City, State"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Flight Experience
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm bg-white cursor-pointer"
                >
                  <option value="">Select your experience level</option>
                  <option value="beginner">New to commercial drone work</option>
                  <option value="intermediate">1-2 years of flight experience</option>
                  <option value="experienced">3-5 years of flight experience</option>
                  <option value="expert">5+ years of flight experience</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Certifications
                </label>
                <input
                  type="text"
                  name="certifications"
                  value={formData.certifications}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="FAA Part 107, state licenses, etc."
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Equipment You Own
                </label>
                <input
                  type="text"
                  name="equipment"
                  value={formData.equipment}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="Drone models, sensors, etc."
                />
              </div>

              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tell Us About Yourself
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm resize-none"
                  placeholder="What draws you to agricultural drone work? What are you looking for in a team?"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white font-bold py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Final CTA */}
      <section className="py-20 topo-cta-bg relative">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Questions Before You Apply?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Not sure if this is the right fit? Reach out anyway. We&apos;re happy to talk through what the
            work looks like and answer any questions you have.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-white text-emerald-700 font-bold px-8 py-4 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Talk With Us First
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
